import { forwardRef, useState } from 'react';
import './RevealableInput.css';

/**
 * Wraps a single text-style input with a small "show / hide" eye
 * toggle on the right. Lets a kid (or grown-up) sanity-check what
 * they typed in a password or PIN field — common-sense affordance
 * that prevents the "I keep failing signin because the auto-corrected
 * something" loop. The toggle is a real <button> so screen readers
 * can announce state changes.
 *
 * Defaults to `type="password"`. Pass `defaultType="tel"` for PIN
 * fields so phones surface the numeric keypad while still hiding the
 * digits behind dots until the kid taps the eye.
 */
export interface RevealableInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  /** The "hidden" input type. Defaults to `password`. PIN fields want
   *  `tel` so phones show the numeric keypad. When the eye is open we
   *  swap to `text` so digits / characters are visible. */
  defaultType?: 'password' | 'tel';
}

export const RevealableInput = forwardRef<HTMLInputElement, RevealableInputProps>(
  function RevealableInput({ defaultType = 'password', className, ...rest }, ref) {
    const [revealed, setRevealed] = useState(false);
    return (
      <span className={`bl-rev${className ? ` ${className}` : ''}`}>
        <input
          ref={ref}
          type={revealed ? 'text' : defaultType}
          className="bl-rev__input"
          {...rest}
        />
        <button
          type="button"
          className="bl-rev__toggle"
          onClick={() => setRevealed((v) => !v)}
          aria-label={revealed ? 'Hide value' : 'Show value'}
          aria-pressed={revealed}
          tabIndex={-1}
        >
          <span aria-hidden="true">{revealed ? '👀' : '🙈'}</span>
        </button>
      </span>
    );
  },
);
